// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, Tag, Truck, ChevronRight, Star, Zap } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

function ProductCard({ product, onAdd }) {
  const [adding, setAdding] = useState(false);
  const isOnSale = product.sale_price_cents && product.sale_price_cents < product.price_cents;
  const price = isOnSale ? product.sale_price_cents : product.price_cents;

  const handleAdd = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAdding(true);
    await onAdd(product);
    setAdding(false);
  };

  return (
    <Link to={`/products/${product.id}`} style={{ textDecoration: 'none' }}>
      <div className="product-card">
        <div className="product-img">
          {product.is_organic && (
            <span style={{
              position: 'absolute', top: 8, left: 8,
              background: '#16A34A', color: '#fff',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            }}>ORGANIC</span>
          )}
          {isOnSale && (
            <span style={{
              position: 'absolute', top: 8, right: 8,
              background: '#EF4444', color: '#fff',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            }}>SALE</span>
          )}
          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getProductEmoji(product)}
          </div>
        </div>
        <div className="product-body">
          <div className="product-name">{product.name}</div>
          <div className="product-unit">{product.unit}</div>
          <div className="product-price-row">
            <div>
              {isOnSale ? (
                <>
                  <span className="product-sale-price">{fmt(product.sale_price_cents)}</span>
                  <span className="product-original-price">{fmt(product.price_cents)}</span>
                </>
              ) : (
                <span className="product-price">{fmt(product.price_cents)}</span>
              )}
            </div>
            <button
              onClick={handleAdd}
              disabled={adding}
              style={{
                width: 32, height: 32, borderRadius: 10,
                background: adding ? '#86EFAC' : '#16A34A',
                border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s', flexShrink: 0,
              }}
            >
              {adding ? <span style={{ color: '#fff', fontSize: 14 }}>✓</span> : <ShoppingCart size={16} color="#fff" />}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { addItem }       = useCart();
  const { user, customer } = useAuth();
  const navigate          = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured]     = useState([]);
  const [coupons, setCoupons]       = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [{ data: cats }, { data: prods }, { data: coups }] = await Promise.all([
      supabase.from('grocery_categories').select('*').eq('active', true).order('sort_order'),
      supabase.from('grocery_products').select('*').eq('is_featured', true).eq('in_stock', true).eq('active', true).limit(8),
      supabase.from('grocery_coupons').select('*').eq('active', true).limit(4),
    ]);
    setCategories(cats || []);
    setFeatured(prods || []);
    setCoupons(coups || []);
    setLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const tier = customer?.grocery_loyalty_tiers;

  return (
    <div>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #14532D 0%, #15803D 50%, #16A34A 100%)',
        color: '#fff', padding: '56px 24px', textAlign: 'center',
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, lineHeight: 1.15, marginBottom: 12 }}>
            Fresh Groceries,<br />Delivered to Your Door
          </h1>
          <p style={{ fontSize: 18, color: '#BBF7D0', marginBottom: 28 }}>
            Local produce, everyday essentials, and exclusive member deals.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-xl" style={{ background: '#fff', color: '#16A34A', fontWeight: 800 }} onClick={() => navigate('/products')}>
              Shop Now
            </button>
            {!user && (
              <button className="btn btn-xl btn-outline" style={{ borderColor: '#fff', color: '#fff' }} onClick={() => navigate('/login')}>
                Join for Free
              </button>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 28, marginTop: 32, flexWrap: 'wrap' }}>
            {[
              { icon: <Truck size={16} />, text: 'Free delivery over $75' },
              { icon: <Tag size={16} />,   text: 'Digital coupons daily' },
              { icon: <Star size={16} />,  text: 'Earn loyalty points' },
            ].map(item => (
              <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#BBF7D0', fontSize: 14 }}>
                {item.icon}{item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Loyalty member banner */}
      {user && tier && (
        <div style={{
          background: `linear-gradient(135deg, ${tier.tier_color}22, ${tier.tier_color}10)`,
          border: `1px solid ${tier.tier_color}40`,
          borderRadius: 0,
          padding: '14px 24px',
        }}>
          <div className="page-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>{tier.tier_icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#14532D' }}>
                  Welcome back, {customer?.full_name?.split(' ')[0]}! You're a {tier.tier_name} member.
                </div>
                <div style={{ fontSize: 12, color: '#64748B' }}>
                  {customer?.loyalty_points?.toLocaleString()} points · {tier.discount_percent > 0 ? `${tier.discount_percent}% member discount` : 'Earn 1 point per $1 spent'}
                </div>
              </div>
            </div>
            <Link to="/loyalty" style={{ textDecoration: 'none' }}>
              <button className="btn btn-sm" style={{ background: tier.tier_color + '30', color: '#14532D', border: `1px solid ${tier.tier_color}60` }}>
                View Rewards <ChevronRight size={14} />
              </button>
            </Link>
          </div>
        </div>
      )}

      {/* Coupon banner */}
      {coupons.length > 0 && (
        <div style={{ background: '#FFF7ED', borderTop: '1px solid #FED7AA', borderBottom: '1px solid #FED7AA', padding: '12px 0' }}>
          <div className="page-container" style={{ display: 'flex', alignItems: 'center', gap: 12, overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#C2410C', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              <Zap size={16} />
              Today's Deals:
            </div>
            {coupons.map(c => (
              <Link key={c.id} to="/coupons" style={{ textDecoration: 'none', flexShrink: 0 }}>
                <div style={{
                  background: '#fff', border: '1px solid #FED7AA', borderRadius: 8,
                  padding: '6px 14px', fontSize: 13, fontWeight: 700, color: '#C2410C',
                  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                }}>
                  <Tag size={13} />
                  {c.title}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="page-container">
        {/* Categories */}
        <div className="section">
          <h2 className="section-title">Shop by Category</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
            {categories.map(cat => (
              <Link key={cat.id} to={`/categories/${cat.slug}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  background: '#fff', borderRadius: 16, padding: '18px 12px',
                  textAlign: 'center', cursor: 'pointer', border: '1px solid #BBF7D0',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#16A34A'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(22,163,74,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#BBF7D0'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ fontSize: 36, marginBottom: 8 }}>{getCatEmoji(cat.slug)}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#14532D', lineHeight: 1.2 }}>{cat.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Featured products */}
        {featured.length > 0 && (
          <div className="section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div>
                <h2 className="section-title">Featured Products</h2>
                <p className="section-sub">Handpicked fresh selections</p>
              </div>
              <Link to="/products" style={{ textDecoration: 'none' }}>
                <button className="btn btn-outline btn-sm">View All <ChevronRight size={14} /></button>
              </Link>
            </div>
            <div className="product-grid">
              {featured.map(prod => (
                <ProductCard key={prod.id} product={prod} onAdd={addItem} />
              ))}
            </div>
          </div>
        )}

        {/* Value props */}
        <div className="section">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {[
              { icon: '🚚', title: 'Fast Delivery', body: 'Same-day delivery available for orders placed before 2pm.' },
              { icon: '🌱', title: 'Farm Fresh',    body: 'Locally sourced produce delivered at peak freshness.' },
              { icon: '💰', title: 'Great Prices',  body: 'Weekly specials, digital coupons, and loyalty rewards.' },
              { icon: '🔒', title: 'Secure Checkout', body: 'Encrypted payments and secure account management.' },
            ].map(prop => (
              <div key={prop.title} style={{
                background: '#fff', borderRadius: 16, padding: 24,
                border: '1px solid #BBF7D0', textAlign: 'center',
              }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>{prop.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#14532D', marginBottom: 6 }}>{prop.title}</div>
                <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>{prop.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function getProductEmoji(product) {
  const name = product.name.toLowerCase();
  if (name.includes('banana'))    return '🍌';
  if (name.includes('apple'))     return '🍎';
  if (name.includes('spinach') || name.includes('lettuce')) return '🥬';
  if (name.includes('avocado'))   return '🥑';
  if (name.includes('tomato'))    return '🍅';
  if (name.includes('milk'))      return '🥛';
  if (name.includes('egg'))       return '🥚';
  if (name.includes('yogurt'))    return '🫙';
  if (name.includes('cheese'))    return '🧀';
  if (name.includes('chicken'))   return '🍗';
  if (name.includes('beef') || name.includes('ground')) return '🥩';
  if (name.includes('salmon') || name.includes('seafood')) return '🐟';
  if (name.includes('bread') || name.includes('sourdough')) return '🍞';
  if (name.includes('croissant')) return '🥐';
  if (name.includes('pasta'))     return '🍝';
  if (name.includes('sauce'))     return '🫙';
  if (name.includes('olive oil')) return '🫒';
  if (name.includes('water'))     return '💧';
  if (name.includes('juice'))     return '🍊';
  if (name.includes('coffee'))    return '☕';
  return '🛒';
}

function getCatEmoji(slug) {
  const map = {
    'produce':       '🥦',
    'dairy-eggs':    '🥛',
    'meat-seafood':  '🥩',
    'bakery':        '🍞',
    'frozen':        '❄️',
    'pantry':        '🫙',
    'beverages':     '🥤',
    'snacks':        '🍿',
    'health-beauty': '💊',
    'household':     '🧴',
  };
  return map[slug] || '🛒';
}
